import { useState, useEffect, useCallback, useMemo } from "react";
import { ticketListParams } from "utilities/ticketListParams";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useTickets — CRM list hook for the ticketing inbox (SOUND-493).
 *
 * Clones the useMidApplications shape (useState/useEffect + makeApiRequest,
 * NOT React Query). Maps 1:1 to `GET /tickets`, which is server-scoped and
 * behind requireFeature("ticketing"): internal `{}`, client own+children,
 * partner attributed. Internal callers may pass `filters.companyId` to expand
 * a container (partner -> clients, parent -> self+children).
 *
 * @returns {{tickets: any[], pagination: object, loading: boolean, error: any, refetch: Function}}
 */
export function useTickets({
  filters = {},
  page = 1,
  limit = 25,
  sort = "-updatedAt",
  include, // e.g. "deps" — per-row blocks / blockedByOpen enrichment (SOUND-499)
  // SOUND-690: hold the request until a prerequisite is ready.
  //
  // The Focus board derives its `requestType` filter from the served vocabulary,
  // which arrives asynchronously. For the first render that list is empty, so the
  // filter would be an empty string — the hook omits falsy filters and the server
  // treats a missing requestType as "no filter", so the board would fetch and
  // flash EVERY open ticket (227 in prod) before settling. Skipping is the
  // difference between a brief empty state and a wall of unrelated rows.
  skip = false,
} = {}) {
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const filterParams = useMemo(() => JSON.stringify(ticketListParams(filters)), [filters]);

  const fetchTickets = useCallback(async () => {
    if (!companyId) return;
    // Stay in `loading` rather than reporting an empty result: the caller is
    // waiting on a prerequisite, and "no tickets" would be a lie.
    if (skip) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("sort", sort);
      if (include) params.set("include", include);

      // SOUND-693: every DECLARED tickets filter is forwarded — derived from
      // config/searchConfig.js by ticketListParams, not hand-listed here.
      for (const [k, v] of Object.entries(JSON.parse(filterParams))) {
        params.set(k, v);
      }

      const response = await makeApiRequest(
        "get",
        `${apiRoutes.listTickets}?${params.toString()}`,
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );

      const body = response?.data || {};
      setTickets(Array.isArray(body.data) ? body.data : []);
      setPagination(
        body.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 }
      );
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [
    skip,
    companyId,
    auth?.user?.id,
    auth?.user?.accessToken,
    page,
    limit,
    sort,
    include,
    // A STRING, so stable by value: listing each filter by hand was half of
    // the SOUND-693 bug (a filter missing from the deps never even refetched).
    filterParams,
  ]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return { tickets, pagination, loading, error, refetch: fetchTickets };
}

/**
 * useTicketRequestTypes — fetches the audience-filtered request-type
 * vocabulary from `GET /tickets/request-types`. This is the single source of
 * truth for the CREATE dropdown; the frontend never hardcodes the list.
 *
 * @returns {{requestTypes: {key: string, label: string}[], loading: boolean}}
 */
export function useTicketRequestTypes() {
  const [requestTypes, setRequestTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  useEffect(() => {
    if (!companyId || !auth?.user) return;
    let cancelled = false;
    setLoading(true);
    makeApiRequest("get", apiRoutes.ticketRequestTypes, auth.user, companyId)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.data;
        setRequestTypes(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setRequestTypes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, auth?.user]);

  return { requestTypes, loading };
}

/**
 * useInternalUsers — the internal-user directory (SOUND-526), fetched once from
 * `GET /users?userType=internal` (internal-gated) and filtered client-side. The
 * shared source for the @-mention autocomplete and the watcher picker (the team
 * is small, so one fetch + local filter is snappier than per-keystroke search).
 *
 * @returns {{users: any[], loading: boolean}}
 */
export function useInternalUsers({ enabled = true } = {}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(enabled);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  useEffect(() => {
    if (!enabled || !companyId || !auth?.user) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    makeApiRequest(
      "get",
      `${apiRoutes.getCompanyUsers}?userType=internal&limit=200`,
      auth.user,
      companyId
    )
      .then((res) => {
        if (!cancelled) setUsers(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, companyId, auth?.user]);

  return { users, loading };
}
